import { retrieveDataFromLocalStorage } from "./localStorageUtils";
import type APIService from "../APIs/APIService";
import { ECUCataSelectObjectInterface, ecucCataSelectObjTemplate, type ECUCataSelectOptionClusterInterface } from "../models/ECUCInterfaces";
import type { Dispatch, SetStateAction } from "react";
import { clusterSelectMachineObjTemplate, clusterSelectVariantObjTemplate, type ClusterSelectInterface } from "../models/ClusterSelectInterfaces";
import { ImportProcessData } from "../models/ImportProcessData";
import { selectionCriteriaListTemplate, SRPDSelectionCriteriaListInterface } from "../models/SRPSchema";
import { ECUCSelectDataProcessError } from "./customErrors";


export function getPjKeyFromClusterName(jsonContent: any) {
    // If we only need the first available project key, use a more direct approach
    for (const machine of jsonContent.machineList) {
        for (const variant of machine.variantList) {
            for (const cluster of variant.clusterList) {
                if (cluster.clusterName) {
                    const pjKey = cluster.clusterName.split("_")[0];
                    if (pjKey) {
                        return pjKey;
                    }
                }
            }
        }
    }

    return "";
}

export type TypeMachineToVariantDataMap = Record<string, {
        machineId: string;
        machineName?: string;
        canDiagRxId?: string;
        canDiagTxId?: string;
        machineType?: string;
        variants: TypeVariantToClusterDataMap
    }>

export type TypeVariantToClusterDataMap = Record<string, {
    variantId: string | number;
    machineId: string | number;
    machineVariantId: string | number;
    variantName?: string;
    ucmSubordinateId?: string;
    flashingAdapterAddress?: string;
    logicalDiagnosticAddress?: string;
    arxmlRequired?: boolean;
    otaCapable?: boolean;
    asilRating?: string;
    flashCapability?: string;
    commsProtocol?: string | string[];
    publicTopology?: string;
    privateTopology?: string;
    ethernetTopologies?: string[];
    clusters: any[];
}>

export function getMachineDataMap(
    machineData: { machineId: string; machineName: string; canDiagRxId: string; canDiagTxId: string; machineType: string }[]
) {
    let result = {} as TypeMachineToVariantDataMap;

    for (let machine of machineData){
        result[machine.machineId] = {
            ...machine,
            variants: {}
        }
    }


    return result;
}


export function processClusterListData(clusterList: any, selectionCriteriaList: any[]) {
    return clusterList.map((cluster: any) => processClusterSingleItemData(cluster, selectionCriteriaList));
}

export function processClusterSingleItemData(cluster: any, selectionCriteriaList: any[]) {

    const swInstallationSequence = Number.isNaN(Number(cluster.swInstallationSequence)) ? 1 : Number(cluster.swInstallationSequence);

    return {
        clusterName: cluster.clusterName ?? "",
        changeLevel: "",
        clusterType: cluster.clusterType?.toUpperCase() ?? "",
        configurationType: cluster.configurationType?.toUpperCase() ?? "",
        swInstallationSequence: swInstallationSequence,
        swDownloadMethod: cluster.swDownloadMethod?.toLowerCase() ?? "",
        updateType: cluster.updateType ?? "",
        sessionSequence: cluster.sessionSequence ?? "",
        inhibitRequired: cluster.inhibitRequired ?? false,
        udsMethod: getArraysValue(cluster.udsAuthorizationMethod),
        f111CoreAssemblyNum: getArraysValue(cluster.f111coreAssemblyNums),
        selectionCriteriaList: structuredClone(selectionCriteriaList),
        actionType: "",
    }
}

export function processMachineSingleItemData(machine: any) {
    return {
        machineName: machine.machineName ?? "",
        canDiagRxId: addHexPrefixIfNeeded(machine.canDiagRxId),
        canDiagTxId: addHexPrefixIfNeeded(machine.canDiagTxId),
        machineType: typeof machine.machineType === "string" ? (machine.machineType).toUpperCase() : "",
    }
}

interface RawVariantDataInterface {
    variantId: string | number;
    machineVariantId: string | number;
    machineId: string | number;
    variantName?: string;
    ucmSubordinateId?: string;
    flashingAdapterAddress?: string;
    logicalDiagnosticAddress?: string;
    arxmlRequired?: boolean;
    otaCapable?: boolean;
    asilRating?: string;
    flashCapability?: string;
    commsProtocol?: string | string[];
    publicTopology?: string;
    privateTopology?: string;
    ethernetTopologies?: string[];
    clusters: any[];
}

export interface RawClusterDataInterface {
    clusterId: number;
    clusterName: string;
    clusterType: string;
    machineId: number;
    machineVariantId: number;
    configurationType: string;
    f111CoreAssemblyNum: string[];
    updateType: string;
    udsMethod: string;
    inhibitRequired: boolean;
    sessionSequence: string;
    swInstallationSequence: number;
    swDownloadMethod: string;
    logicalDiagnosticAddress: string;
    ucmSubordinateId: string;
    canDiagRxId: string;
    canDiagTxId: string;
}

export type variantDataMap = Record<string, {variants: TypeVariantToClusterDataMap}>;

export function validateVariantDataForMachines(machineDataObj: TypeMachineToVariantDataMap, variantDataObj: variantDataMap) {
    for (let machineId in machineDataObj) {
        if (!variantDataObj[machineId] || !variantDataObj[machineId].variants) {
            const machineName = machineDataObj[machineId].machineName || "";
            if (machineName) {
                throw new ECUCSelectDataProcessError(`No variant data found for machine ${machineName} with ID ${machineId}`, {
                    machineId,
                    machineName
                });
            } else {
                throw new ECUCSelectDataProcessError(`No variant data found for machine with ID ${machineId}`, {
                    machineId
                });
            }
        }
    }
}

export function validateClusterDataForVariants(variantDataObj: variantDataMap, clusterOptions: ECUCataSelectOptionClusterInterface[]) {
    for (let machineId of Object.keys(variantDataObj)) {
        const variants = variantDataObj[machineId].variants;
        for (let variantId in variants) {

            const matchingClusters = clusterOptions.filter((option) => {
                return String(option.machineId) === String(machineId) && String(option.variantId) === String(variantId);
            });

            if (matchingClusters.length === 0) {
                const variantName = variants[variantId].variantName || "";
                if (variantName) {
                    throw new ECUCSelectDataProcessError(`No cluster data found for variant ${variantName} with ID ${variantId}`, {
                        machineId,
                        variantId,
                        variantName
                    });
                } else {
                    throw new ECUCSelectDataProcessError(`No cluster data found for variant with ID ${variantId}`, {
                        machineId,
                        variantId
                    });
                }
            }
        }
    }
}

export function getVariantDataMap(variantData: RawVariantDataInterface[]) {
    const variantDataObj = {} as variantDataMap;

    for (const variant of variantData){
        if (!variantDataObj[variant.machineId]){variantDataObj[variant.machineId] = {variants:{}}}
        variantDataObj[variant.machineId]["variants"][variant.machineVariantId] = variant; 
    }

    return variantDataObj;
}

export function sortVehicleProgramsByModelYear(vehiclePrograms: string[]): string[] {
    const getModelYear = (vehicleProgram: string) => parseInt(vehicleProgram.split("-")[0], 10);

    return [...vehiclePrograms].sort((a, b) => {
        const yearDiff = getModelYear(a) - getModelYear(b);
        return yearDiff !== 0 ? yearDiff : a.localeCompare(b);
    });
}

export function getProgramDataMap(data: any){
    const programData = {
        vehiclePrograms: [],
        architectureType:"",
        electricalArchitectures: [],
        lveas: []
    } as {
        vehiclePrograms: string[],
        architectureType: string,
        electricalArchitectures: string[],
        lveas: string[]
    }
    
    const lveaSet = new Set<string>([]);
    const electricalArchitectureSet = new Set<string>([]);
    programData.vehiclePrograms = sortVehicleProgramsByModelYear(data.map((program: any) => program.modelYearProgramCode));
    programData.architectureType = data?.at(0)?.electricalArchitecture;

    data.forEach((program: any) => {
        lveaSet.add(`${program.lvea}`);
        if (program.electricalArchitecture) {
            electricalArchitectureSet.add(program.electricalArchitecture);
        }
    });
    programData.lveas = Array.from(lveaSet);
    // Distinct electrical architectures across all selected programs (e.g. both FNV3.2 and FNV3.4).
    programData.electricalArchitectures = Array.from(electricalArchitectureSet);
    return programData;
}

export function getModuleDataMap(moduleData: { moduleName: string, moduleDescription: string }) {
    const processedModuleData = {
        moduleName: moduleData[0].moduleName ?? "",
        moduleDescription: moduleData[0].moduleDescription ?? "",
    } as {
        moduleName: string;
        moduleDescription: string;
    };

    return processedModuleData;
}

const addHexPrefixIfNeeded = (value?: string): string => {
    if (value?.includes("0x")) {
        return value;
    }
    
    return value ? "0x" + value : "";
};

const getArraysValue = (ArrayItem?: string | string[]): string[] => {
    if (Array.isArray(ArrayItem)) {
        // if contains a null value, remove it
        ArrayItem = ArrayItem.filter(item => item !== null && item !== undefined); 
        return ArrayItem;
    }
    
    return ArrayItem ? [ArrayItem] : [];
};

export function processVariantSingleItemData (variant: RawVariantDataInterface) {
    return {
        variantName: variant.variantName ?? "",
        ucmSubordinateId: variant.ucmSubordinateId ?? "",
        flashingAdapterAddress: addHexPrefixIfNeeded(variant.flashingAdapterAddress),
        logicalDiagAddress: addHexPrefixIfNeeded(variant.logicalDiagnosticAddress),
        arxmlRequired: variant.arxmlRequired ?? false,
        otaCapable: variant.otaCapable ?? false,
        asilRating: variant.asilRating ?? "",
        flashCapability: variant.flashCapability ?? "",
        commsProtocol: getArraysValue(variant.commsProtocol),
        publicTopology: variant.publicTopology ?? "",
        privateTopology: variant.privateTopology ?? "",
        ethernetTopology: getArraysValue(variant.ethernetTopologies),
    }
}

export function prepClusterSelectObj ({
        clusterOptions, 
        machineToVariantData,
        setClusterSelectObj,
        importProcessData,
    }: {
        clusterOptions: ECUCataSelectOptionClusterInterface[],
        machineToVariantData: TypeMachineToVariantDataMap
        setClusterSelectObj: Dispatch<SetStateAction<ClusterSelectInterface[]>>,
        importProcessData: ImportProcessData,
    }) {
        //recieve machine and variant list (combined ver?)
        //sets up the cluster select object, gets the options for each and inserts them.


        const transferObj = machineToVariantData;


        console.log(JSON.stringify(clusterOptions,null,'   '));
        console.log(JSON.stringify(transferObj, null, '   '))

    
        for(let cluster of clusterOptions){
            console.log(JSON.stringify(cluster, null, '   '));

            
            if(!transferObj[cluster.machineId]['variants'][cluster.variantId]['clusters']){
                transferObj[cluster.machineId]['variants'][cluster.variantId]['clusters'] = [];
            }
            transferObj[cluster.machineId]['variants'][cluster.variantId]['clusters'].push(cluster)
                
        }

        console.log(JSON.stringify(transferObj, null, '   '))

        // Original clusterSelectobj setup logic
        const newClusterSelectObj = [] as ClusterSelectInterface[];
        for ( let machineKey in transferObj){
            const machineObj = structuredClone(clusterSelectMachineObjTemplate)
            
            machineObj.label = transferObj[machineKey]["machineName"] ?? "";
            machineObj.description = transferObj[machineKey]["machineDescription"] ?? "";

            for (let variantKey in transferObj[machineKey]['variants']){
                const variantObj = structuredClone(clusterSelectVariantObjTemplate);

                variantObj.logicalAddress = transferObj[machineKey]['variants'][variantKey]["logicalDiagnosticAddress"] ?? "";
                variantObj.label = transferObj[machineKey]['variants'][variantKey]["variantName"] ?? "";
                variantObj.description = transferObj[machineKey]['variants'][variantKey]["variantDescription"] ?? "";   
                
                variantObj.notAddedClusters = transferObj[machineKey]['variants'][variantKey]['clusters'] ?? [];

                // Add configs here when fetched
                variantObj.configs = [];

                machineObj.variantList.push(variantObj);
            }

            newClusterSelectObj.push(machineObj);
        }

        if (importProcessData.enabled) {
            // Pre-select clusters based on importProcessData
            processClusterSelectObjForImportedData();
        }
        setClusterSelectObj(newClusterSelectObj);

    function processClusterSelectObjForImportedData() {
        const machineList = importProcessData.data.machineList;

        for (let machine of newClusterSelectObj) {
            const importedMachine = machineList.find((m: any) => m.machineName === machine.label);
            if (!importedMachine) {
                continue;
            }
            for (let variant of machine.variantList) {
                const importedVariant = importedMachine.variantList.find((v: any) => v.variantName === variant.label);
                if (!importedVariant) {
                    continue;
                }
                const importedClusterNames = importedVariant.clusterList.map((cluster: any) => cluster.clusterName);
                const importedClusterNameSet = new Set(importedClusterNames);
                
                const toBeMovedClusters = variant.notAddedClusters.filter(
                    (cluster: any) => importedClusterNameSet.has(cluster.label)
                );

                // Remove clusters that are in importedClusterNames from notAddedClusters
                variant.notAddedClusters = variant.notAddedClusters.filter(
                    (cluster: any) => !importedClusterNameSet.has(cluster.label)
                );

                // Add imported clusters to addedClusters and pendingAdd
                for (let cluster of toBeMovedClusters) {
                    cluster.isImported = true;
                    variant.addedClusters.push(cluster);
                    variant.pendingAdd.push(cluster);
                }
            }
        }
    }
}

export function getSelectionCriteriaList(programs: any[]) {

    const selectionCriteriaList = [];
    for (const program of programs) {
        let programCode = "";
        let modelYear = "";

        let splits = program.split("-");
        if (splits.length >= 2){
            modelYear = splits[0];
            programCode = splits[1];
        }
        selectionCriteriaList.push({
            ...selectionCriteriaListTemplate,
            programCode: programCode,
            modelYear: modelYear
        });
    }
    return selectionCriteriaList;
}



export const getInitializedEcuCataObj = async ({
    bypassLoading = false, 
    setECUCataSelectObj,
    apiService
}: {
    bypassLoading?: boolean,
    setECUCataSelectObj: (obj: any) => void,
    apiService: APIService
}) => {
    try {
    const ecuCataSelectObjFromStorage =  retrieveDataFromLocalStorage('ecuCataSelectObj');

    const initEcuCataObj = structuredClone(ecucCataSelectObjTemplate);

    const formProcessingStateFromStorage = retrieveDataFromLocalStorage('formProcessingState');
    if (formProcessingStateFromStorage !== "ECUC_SELECT" && !!ecuCataSelectObjFromStorage) {
        setECUCataSelectObj(ecuCataSelectObjFromStorage);
        return;
    }

    if(bypassLoading){
        setECUCataSelectObj(initEcuCataObj);
        return;
    }

    const programOptionsResponse = await apiService.getProgramOptions([]);

    if (!programOptionsResponse.isSuccess) {
        console.error("Failed to fetch program options:", programOptionsResponse.message);
        setECUCataSelectObj(initEcuCataObj);
        return;
    }
    const data = programOptionsResponse.data;

    console.log("Fetched program options:", data);

    const programOptions = Array.isArray(data) ? data.map((option) => ({
        value: option.value,
        label: option.label,
        electricalArchitecture: "",
    })) : [];

    const programDataListResponse = await apiService.getProgramData(programOptions.map((option) => option.value) || []);

    if (!programDataListResponse.isSuccess) {
        console.error("Failed to fetch program data:", programDataListResponse.message);
        setECUCataSelectObj(initEcuCataObj);
        return;
    }
    
    for (const item of programDataListResponse.data) {
        const matchingOption = programOptions.find((option) => option.label === item.modelYearProgramCode);
        if (matchingOption) {
            matchingOption.electricalArchitecture = item.electricalArchitecture;
        }
    }
    initEcuCataObj.programs.unfilteredOptions = structuredClone(programOptions);
    initEcuCataObj.programs.options = structuredClone(programOptions);
    setECUCataSelectObj(initEcuCataObj);

} catch (error) {
    console.error("Error initializing ECU Catalog:", error);
    const initEcuCataObj = structuredClone(ecucCataSelectObjTemplate);

    initEcuCataObj.error = true;
    initEcuCataObj.errorMessage = "Failed to initialize ECU Catalog. Please try again later. If the problem persists, contact support.";
    setECUCataSelectObj(initEcuCataObj);
}
}

export function reorderClusterListBySwInstallationSequence (srp: {
    machineList: {
        variantList: {
            clusterList: {
                swInstallationSequence: number;
            }[];
        }[];
    }[];
}) {
    // Sort the clusterList by swInstallationSequence
        // Reorder clusters in each machine and variant by swInstallationSequence value
    for (const machine of srp.machineList) {
        for (const variant of machine.variantList) {
            const clusters = structuredClone(variant.clusterList);
            clusters.sort((a: {
                swInstallationSequence: number;
            } , b: {
                swInstallationSequence: number;
            } ) => {
                return a.swInstallationSequence - b.swInstallationSequence;
            });
            variant.clusterList = clusters;
        }
    }
}

    // Helper function to create selection criteria list from vehicle programs
export function createSelectionCriteriaList(vehiclePrograms: string[]): SRPDSelectionCriteriaListInterface[] {
        return vehiclePrograms.map((program: string) => {
            let programCode = "";
            let modelYear = "";

            let splits = program.split("-");
            if (splits.length >= 2) {
                modelYear = splits[0];
                programCode = splits[1];
            }

            return {
                ...structuredClone(selectionCriteriaListTemplate),
                programCode: programCode,
                modelYear: modelYear,
            }
        });
    }   


export function addClustersOptionsToMachineToVariantData (
    clusterOptions: ECUCataSelectOptionClusterInterface[],
    machineToVariantData: TypeMachineToVariantDataMap
) {
    for (let cluster of clusterOptions) {
        if (!machineToVariantData[cluster.machineId]['variants'][cluster.variantId]['clusters']) {
            machineToVariantData[cluster.machineId]['variants'][cluster.variantId]['clusters'] = [];
        }
        machineToVariantData[cluster.machineId]['variants'][cluster.variantId]['clusters'].push(cluster);
    }
}


    // Helper function to fetch and process cluster data
export async function fetchAndProcessClusterData(
    clusterId: any,
    ecuCataSelectObj: ECUCataSelectObjectInterface,
    selectionCriteriaList: SRPDSelectionCriteriaListInterface[],
    apiService: APIService
) {
    if (!apiService) {
        throw new Error("ECUC API is not initialized");
    }
    let clusterDataResponse = await apiService.getClusterData(
        ecuCataSelectObj.programs.value.map((program) => program.value),
        ecuCataSelectObj.module.value.value,
        ecuCataSelectObj.machines.value.map((machine) => machine.value),
        ecuCataSelectObj.variants.value.map((variant) => variant.value),
        [clusterId]
    );

    if (!clusterDataResponse.isSuccess) {
        console.warn(`Failed to fetch cluster data for cluster ${clusterId} using primary method:`, clusterDataResponse.message);
        
        // Attempt to fetch using alternative method
        clusterDataResponse = await apiService.getClusterDataAlternative(
            clusterId,
        );

        if (!clusterDataResponse.isSuccess) {
            throw new Error(clusterDataResponse.message || "Failed to fetch cluster data using alternative method");
        }
    }

    const clusterData = Array.isArray(clusterDataResponse.data) ? clusterDataResponse.data[0] : clusterDataResponse.data;
    return processClusterSingleItemData(clusterData, selectionCriteriaList);
}

export async function fetchProcessedClusterDataForVariant({
    machineId,
    variantId,
    variant,
    ecuCataSelectObj,
    selectionCriteriaList,
    apiService,
}: {
    machineId: string;
    variantId: string;
    variant: any;
    ecuCataSelectObj: ECUCataSelectObjectInterface;
    selectionCriteriaList: SRPDSelectionCriteriaListInterface[];
    apiService: APIService;
}) {
    const clusterOptionsForVariant = Object.values(variant?.clusters ?? {}) as Array<{
        value: string | number;
        label?: string;
    }>;
    const clusterIds = clusterOptionsForVariant
        .map((clusterOption) => clusterOption?.value)
        .filter((clusterValue) => clusterValue !== undefined && clusterValue !== null)
        .map((clusterValue) => String(clusterValue));

    if (clusterIds.length === 0) {
        return [];
    }

    const moduleId = ecuCataSelectObj.module.value?.value;
    if (!moduleId) {
        throw new Error("Missing module ID while fetching cluster data");
    }

    const programIds = ecuCataSelectObj.programs.value.map((program) => program.value);

    const bulkResponse = await apiService.getClusterData(
        programIds,
        moduleId,
        [String(machineId)],
        [String(variantId)],
        clusterIds
    );

    const processedClusterDataById = new Map<string, any>();
    const unresolvedClusterIds = new Set(clusterIds);

    if (bulkResponse.isSuccess && Array.isArray(bulkResponse.data)) {
        const processedClusterDataByName = new Map<string, any>();

        for (const rawClusterData of bulkResponse.data) {
            const processedClusterData = processClusterSingleItemData(rawClusterData, selectionCriteriaList);
            const resolvedClusterId = rawClusterData?.clusterId;

            if (resolvedClusterId !== undefined && resolvedClusterId !== null) {
                const normalizedClusterId = String(resolvedClusterId);
                processedClusterDataById.set(normalizedClusterId, processedClusterData);
                unresolvedClusterIds.delete(normalizedClusterId);
            }

            if (typeof rawClusterData?.clusterName === "string" && rawClusterData.clusterName !== "") {
                processedClusterDataByName.set(rawClusterData.clusterName, processedClusterData);
            }
        }

        // Backfill identifier mismatches by matching requested option label to clusterName.
        for (const clusterOption of clusterOptionsForVariant) {
            const normalizedClusterId = String(clusterOption.value);
            if (!unresolvedClusterIds.has(normalizedClusterId)) {
                continue;
            }

            if (!clusterOption.label) {
                continue;
            }

            const processedByName = processedClusterDataByName.get(clusterOption.label);
            if (!processedByName) {
                continue;
            }

            processedClusterDataById.set(normalizedClusterId, processedByName);
            unresolvedClusterIds.delete(normalizedClusterId);
        }
    }

    const fallbackClusterIds = bulkResponse.isSuccess
        ? Array.from(unresolvedClusterIds)
        : clusterIds;

    if (!bulkResponse.isSuccess) {
        console.warn(`Bulk cluster fetch failed for machine ${machineId}, variant ${variantId}. Falling back per cluster:`, bulkResponse.message);
    }

    for (const clusterOption of clusterOptionsForVariant) {
        const normalizedClusterId = String(clusterOption.value);
        if (!fallbackClusterIds.includes(normalizedClusterId)) {
            continue;
        }

        const processedClusterData = await fetchAndProcessClusterData(
            clusterOption.value,
            ecuCataSelectObj,
            selectionCriteriaList,
            apiService
        );
        processedClusterDataById.set(normalizedClusterId, processedClusterData);
    }

    return clusterOptionsForVariant
        .map((clusterOption) => processedClusterDataById.get(String(clusterOption.value)))
        .filter(Boolean);
}